import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { postRegister } from '../../lib/api/auth';
import type { AuthUser } from '../../lib/auth/types';
import { SF_PRIVACY, SF_TERMS } from '../../lib/externalLinks';
import { FREE_PLAY_WEEK } from '../../lib/eventState';
import { useFocusOnMount } from '../../lib/hooks/useFocusOnMount';
import { Button } from '../Button';
import { BackLink } from './BackLink';
import { FormError } from '../form/FormError';
import { Toggle } from '../form/Toggle';
import { HeadingUnderscore, RegisterLayout } from './RegisterLayout';

/**
 * Step 5 — first-time registration consents (Figma /reg-5).
 *
 * Four toggles:
 *   - Age (required)                  → ageConfirmed: true
 *   - Terms & Privacy (required)      → termsAccepted: true + termsVersion
 *   - Contest Rules (required)    → rulesConsent { weekNumber, rulesVersion }
 *   - Marketing (optional)            → marketingConsent
 *
 * Required toggles disable the Play button until all three are on. The
 * marketing toggle stays freely toggleable.
 */
type ConsentStepProps = {
  weekNumber: number;
  rulesVersion: string;
  termsVersion: string;
  firstName: string;
  lastName: string;
  country: string;
  onBack: () => void;
  onComplete: (user: AuthUser) => void;
};

export function ConsentStep({
  weekNumber,
  rulesVersion,
  termsVersion,
  firstName,
  lastName,
  country,
  onBack,
  onComplete,
}: ConsentStepProps) {
  const [age, setAge] = useState(false);
  const [terms, setTerms] = useState(false);
  const [rules, setRules] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Set true after register succeeds so the user can't re-tap Play while
  // the parent navigates away (this step doesn't have an AnimatePresence
  // exit fade — `onComplete` navigates via react-router — but a slow nav
  // would still leave the button live without this guard).
  const [transitioning, setTransitioning] = useState(false);
  const busy = pending || transitioning;
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  // Post-contest Free Play (GHS5-85): the weekly-rules consent points at the
  // Free Play Terms and Conditions (week 6+), so reword the contest-specific copy.
  const isFreePlay = weekNumber >= FREE_PLAY_WEEK;

  const allRequired = age && terms && rules;

  async function handleSubmit(): Promise<void> {
    if (!allRequired || busy) return;
    setBannerError(null);
    setPending(true);
    try {
      const user = await postRegister({
        firstName,
        lastName,
        country,
        ageConfirmed: true,
        termsAccepted: true,
        termsVersion,
        marketingConsent: marketing,
        rulesConsent: {
          weekNumber,
          rulesVersion,
          clientTs: new Date().toISOString(),
        },
      });
      setTransitioning(true);
      onComplete(user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Could be already_registered OR rules_version_mismatch — both 409 from
        // the API. The body code disambiguates.
        const code = (err.body as { code?: string } | undefined)?.code;
        if (code === 'rules_version_mismatch') {
          setBannerError(
            `The ${isFreePlay ? 'Free Play conditions' : 'contest rules'} have been updated. Please refresh and re-read before agreeing.`,
          );
        } else {
          setBannerError("Looks like you're already registered. Try signing in instead.");
        }
      } else if (err instanceof ApiError && err.status >= 500) {
        setBannerError('Something went wrong on our end. Please try again.');
      } else {
        setBannerError('Network error. Check your connection and try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <RegisterLayout
      heading={
        <>
          register
          <HeadingUnderscore />
        </>
      }
    >
      <BackLink onClick={onBack} disabled={busy} />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-center font-['Orbitron',sans-serif] text-[clamp(15px,4.6vw,20px)] uppercase tracking-[0.1em] text-white focus:outline-none"
      >
        {isFreePlay ? 'Complete Your Registration' : 'Complete Your Contest Registration'}
      </h2>

      <form
        className="flex flex-col items-stretch gap-[18px]"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        noValidate
      >
        <Toggle
          checked={age}
          onChange={setAge}
          required
          disabled={busy}
          label={
            <>
              I am 18 years or older.{' '}
              <span className="text-white/75">
                (For AL, DE, and NE residents you must be 19 years or older and for MS residents
                you must be 21 years or older.)
              </span>
            </>
          }
        />

        <Toggle
          checked={terms}
          onChange={setTerms}
          required
          disabled={busy}
          label={
            <>
              I have reviewed and agree to the{' '}
              <a
                href={SF_TERMS}
                target="_blank"
                rel="noreferrer noopener"
                className="underline hover:text-[#00ffb2]"
              >
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a
                href={SF_PRIVACY}
                target="_blank"
                rel="noreferrer noopener"
                className="underline hover:text-[#00ffb2]"
              >
                Privacy Policy
              </a>
              .
            </>
          }
        />

        <Toggle
          checked={rules}
          onChange={setRules}
          required
          disabled={busy}
          label={
            <>
              I have reviewed and agree to the{' '}
              <a
                href="/rules"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#00ffb2]"
              >
                {isFreePlay ? 'Free Play Terms and Conditions' : 'Official Gamerhood Contest Rules'}
              </a>
              .
            </>
          }
        />

        <Toggle
          checked={marketing}
          onChange={setMarketing}
          disabled={busy}
          label={
            <>
              I understand and consent to State Farm using my personal information that I provide
              in my Contest Registration for business, marketing, and commercial purposes
              consistent with State Farm Privacy Policy accessible at{' '}
              <a
                href={SF_PRIVACY}
                target="_blank"
                rel="noreferrer noopener"
                className="underline hover:text-[#00ffb2]"
              >
                statefarm.com/customer-care/privacy-security/privacy
              </a>
              . Use for marketing may include direct and indirect communications from State Farm
              and/or a third party acting on behalf of State Farm.
            </>
          }
        />

        <FormError>{bannerError}</FormError>

        <div className="flex flex-col items-center gap-[16px]">
          <Button
            variant="dark"
            type="submit"
            disabled={!allRequired || busy}
            className="min-w-[200px]"
          >
            {pending ? 'Saving…' : 'Play'}
          </Button>
          {/* {!allRequired && (
            <p className="font-['Arial',sans-serif] text-[12px] text-white/70">
              All three required agreements must be enabled to continue.
            </p>
          )} */}
        </div>
      </form>
    </RegisterLayout>
  );
}
